import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MdAdd,
  MdArrowBack,
  MdArrowForward,
  MdClose,
  MdFileDownload,
  MdKeyboardArrowDown,
  MdRefresh,
  MdSearch,
  MdPeopleOutline,
  MdFilterAlt,
  MdSave,
  MdSend,
} from "react-icons/md";
import { FiRefreshCw, FiExternalLink } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { createPortal } from "react-dom";
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Line, LabelList } from "recharts";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuthLogged,
  selectStyles,
  handleSessionExpired,
  fetchApplicationAppId,
  fetchAjaxListData,
  getBrowserTimeZone,
  getWeekMonday,
  parseLocalIsoDate,
  toLocalIsoDate,
  getDateRangeForPreset,
  DATE_RANGE_PRESETS,
  formatDateTime,
  exportListCsv,
  bigAmountFormat,
  countFormat,
  getAuthUserCurrency,
  can,
  labelizeKey,
  FUNNEL_STEP_SEPARATOR,
  FUNNEL_MIN_STEPS,
  FUNNEL_MAX_STEPS,
  FUNNEL_USERS_PAGE_SIZE,
  FUNNEL_SEGMENT_USER_CAP,
  FUNNEL_BREAKDOWN_OPTIONS,
  FUNNEL_MODE_CODES,
  FUNNEL_SOURCE_OPTIONS,
  FUNNEL_WINDOW_OPTIONS,
  FUNNEL_EVENT_COLORS,
  CHART_AXIS_STEP_MULTIPLIERS,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import HelpTooltip from "../common/HelpTooltip";
import FunnelDetailsModal from "./FunnelDetailsModal";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import SkeletonLoader from "../common/SkeletonLoader";
import ListPaginationBar from "../common/ListPaginationBar";
import "./engage.css";


const KPI_HELP = {
  started:
    "Users who did the first step during the selected date range. Every other number on this page is counted from these users.",
  completed: "Users who went through every step of the funnel (within the time limit, if one is set).",
  conversion: "Completed ÷ Started × 100. Out of everyone who started, how many finished the whole funnel.",
  drop: "The step-to-step move where the most users were lost. This is usually the first place to improve.",
  time: "Average time users took from the first step to the last step. Only users who completed the funnel are counted. Median is the middle value: half the users were faster, half slower.",
};

/* Saved funnel <-> page state. Filters keep the date PRESET (e.g. last_30_days) so a saved funnel
   always opens on fresh dates; exact dates are stored only for a custom range. */
function filtersToSaved(f) {
  const preset = f.datePreset && f.datePreset !== "custom" ? f.datePreset : "";
  return {
    date_preset: preset || (f.dateFrom && f.dateTo ? "custom" : ""),
    date_from: preset ? "" : f.dateFrom || "",
    date_to: preset ? "" : f.dateTo || "",
    campaign_id: f.campaign_id || "",
    is_organic: f.is_organic === "" || f.is_organic == null ? "" : String(f.is_organic),
    country: f.country || "",
    platform: f.platform || "",
    state: f.state || "",
    city: f.city || "",
    breakdown: f.breakdown || "",
    exclude_events: f.exclude_events || "",
  };
}

function savedToFilters(saved = {}) {
  let f = { ...getDefaultFilters(), dateFrom: "", dateTo: "", datePreset: "" };
  if (saved.date_preset && saved.date_preset !== "custom" && DATE_RANGE_PRESETS.some((x) => x.id === saved.date_preset)) {
    const r = getDateRangeForPreset(saved.date_preset);
    f = { ...f, dateFrom: r.dateFrom, dateTo: r.dateTo, datePreset: r.preset };
  } else if (saved.date_from && saved.date_to) {
    f = { ...f, dateFrom: saved.date_from, dateTo: saved.date_to, datePreset: "custom" };
  } else {
    f = { ...f, ...getDefaultFilters() };
  }
  return {
    ...f,
    campaign_id: saved.campaign_id != null && saved.campaign_id !== "" ? String(saved.campaign_id) : "",
    is_organic: saved.is_organic != null && saved.is_organic !== "" ? String(saved.is_organic) : "",
    country: saved.country || "",
    platform: saved.platform || "",
    state: saved.state || "",
    city: saved.city || "",
    breakdown: saved.breakdown || "",
    exclude_events: saved.exclude_events || "",
  };
}

/** Comparable snapshot of what gets saved — used to show "unsaved changes". */
function configKey({ name, description, steps, mode, windowDays, filters }) {
  return JSON.stringify({
    n: String(name || "").trim(),
    d: String(description || "").trim(),
    s: steps.map((x) => String(x || "").trim()),
    m: mode,
    w: String(windowDays || ""),
    f: filtersToSaved(filters),
  });
}

/** "A, B +1": first two names, then how many more. */
function shortList(names) {
  return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
}

/** Multi-event step: its events with users and a color (fixed by the event's place in the step). */
function stepEvents(d) {
  if (!d?.by_event || d.by_event.length < 2) return null;
  const order = splitStep(d.event);
  return d.by_event.map((e) => ({ ...e, color: FUNNEL_EVENT_COLORS[Math.max(0, order.indexOf(e.event)) % FUNNEL_EVENT_COLORS.length] }));
}

function StepEventList({ events }) {
  return (
    <div className="funnel-event-list">
      {events.map((e) => (
        <span key={e.event}>
          <i style={{ background: e.color }} />
          {labelizeKey(e.event)} <b>{countFormat(e.users)}</b>
        </span>
      ))}
    </div>
  );
}

/**
 * Popover under a command-bar button. Opens to the side and direction that fit on screen:
 * right-aligned when there is no room on the right, upwards when there is no room below.
 */
function FunnelPopover({ className = "", prefer = "left", children, ...rest }) {
  const ref = useRef(null);
  const [place, setPlace] = useState({ x: prefer, y: "down" });
  const update = useCallback(() => {
    const el = ref.current;
    const anchor = el?.parentElement?.getBoundingClientRect();
    if (!el || !anchor) return;
    // stay inside the page content area (so it never slides under the left sidebar)
    const area = (el.closest(".dashboard_right") || document.documentElement).getBoundingClientRect();
    const minX = Math.max(8, area.left + 8);
    const maxX = Math.min(window.innerWidth, area.right) - 8;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const fitsLeft = anchor.left + w <= maxX; // opening from the anchor's left edge
    const fitsRight = anchor.right - w >= minX; // opening from the anchor's right edge
    const x = prefer === "left" ? (fitsLeft || !fitsRight ? "left" : "right") : fitsRight || !fitsLeft ? "right" : "left";
    const y = anchor.bottom + 8 + h <= window.innerHeight - 8 || anchor.top - 8 - h < 8 ? "down" : "up";
    setPlace((p) => (p.x === x && p.y === y ? p : { x, y }));
  }, [prefer]);
  useLayoutEffect(() => {
    update();
    const el = ref.current;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (ro && el) {
      ro.observe(el);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [update]);
  return (
    <div ref={ref} className={`funnel-pop funnel-pop--${place.x} funnel-pop--${place.y} ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Filters popover groups (and which filter keys each one holds, for its count). */
const FILTER_GROUPS = [
  { key: "audience", label: "Audience", keys: ["campaign_id", "is_organic", "platform"] },
  { key: "location", label: "Location", keys: ["country", "state", "city"] },
  { key: "exclude", label: "Exclude", keys: ["exclude_events"] },
];

/** Compact dropdowns in the command bar: same theme, button height (36px) and radius. */
const barSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: 36, height: 36, borderRadius: 4 }),
  valueContainer: (base) => ({ ...selectStyles.valueContainer(base), paddingTop: 0, paddingBottom: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  menuPortal: (base) => ({ ...selectStyles.menuPortal(base), minWidth: 200 }),
};

// Amount (revenue) of an Event Metric step, in the user's currency.
const money = (v) => bigAmountFormat(v, getAuthUserCurrency());

function pct(v) {
  const n = Number(v) || 0;
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

const splitStep = (s) => String(s || "").split(FUNNEL_STEP_SEPARATOR).filter(Boolean);

/** "2,218 users" (exact). Pass bigAmountFormat as fmt for the compact KPI tiles ("2.2K users"). */
function plural(n, word, fmt = countFormat) {
  return `${fmt(n)} ${word}${Number(n) === 1 ? "" : "s"}`;
}

function formatDuration(ms) {
  if (ms == null) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} sec`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = min / 60;
  if (hr < 24) return `${Math.round(hr * 10) / 10} hr`;
  const d = Math.round((hr / 24) * 10) / 10;
  return `${d} day${d === 1 ? "" : "s"}`;
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-06-08" -> "8 Jun" (days / weeks) or "Jun 2026" (months). Parsed as plain text so no timezone shift. */
function trendLabel(date, unit, withYear = false) {
  const [y, m, d] = String(date).split("-").map(Number);
  if (!y || !m) return String(date);
  if (unit === "month") return `${MONTHS_SHORT[m - 1]} ${y}`;
  return `${d} ${MONTHS_SHORT[m - 1]}${withYear ? ` ${y}` : ""}`;
}

function getDefaultFilters() {
  const range = getDateRangeForPreset("last_7_days");
  return {
    campaign_id: "",
    is_organic: "",
    country: "",
    platform: "",
    state: "",
    city: "",
    breakdown: "",
    exclude_events: "",
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    datePreset: range.preset,
  };
}

/* ---------------------------------------------------------------- chart -- */

function FunnelChart({ data, onOpen }) {
  const [hover, setHover] = useState(null);
  const openAndClear = (i, kind) => {
    setHover(null);
    onOpen(i, kind);
  };
  const wrapRef = useRef(null);
  const [unit, setUnit] = useState("users"); // Y axis: "users" (counts) or "pct" (% of step 1)
  const first = data[0]?.users || 0;
  // Users: about 4 grid lines with a round gap. rawStep = step-1 users / 4; pow = its 10^n (37,000 → 9,250 → 1,000);
  // gap = first of 1k, 2k, 2.5k, 5k, 10k that is ≥ 9,250 → 10,000 → axis 0–40k. Works for any size; 2.5 × 1 is skipped
  // (no half users). %: 0–100 in 25s.
  const rawStep = Math.max(first / 4, 1);
  const pow = 10 ** Math.floor(Math.log10(rawStep));
  const step = unit === "pct" ? 25 : CHART_AXIS_STEP_MULTIPLIERS.map((m) => m * pow).find((v) => v >= rawStep && Number.isInteger(v)) || 10 * pow;
  const axisMax = unit === "pct" ? 100 : Math.max(step, Math.ceil(first / step) * step);
  const ticks = [];
  for (let t = axisMax; t >= 0; t -= step) ticks.push(t);
  const valueOf = (users) => (unit === "pct" ? (first ? (users / first) * 100 : 0) : users); // bar value on the current axis

  const showTip = (e, i, part) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    // keyboard focus has no mouse position: place the tip at the top centre of the focused bar
    const r = e.clientX == null ? e.currentTarget.getBoundingClientRect() : null;
    const cx = r ? r.left + r.width / 2 : e.clientX;
    const cy = r ? r.top : e.clientY;
    setHover({ i, part, x: cx - box.left, y: cy - box.top, w: box.width });
  };

  return (
    <div className="funnel-chart" ref={wrapRef} onMouseLeave={() => setHover(null)}>
      <div className="funnel-chart-toolbar">
        <div className="funnel-seg funnel-seg--sm" role="group" aria-label="Show bars as">
          {[["users", "Users"], ["pct", "% of step 1"]].map(([u, label]) => (
            <button key={u} type="button" className={unit === u ? "active" : ""} aria-pressed={unit === u} onClick={() => setUnit(u)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="funnel-chart-plot">
        <div className="funnel-chart-yaxis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ bottom: `${(t / axisMax) * 100}%` }}>{unit === "pct" ? `${t}%` : bigAmountFormat(t)}</span>
          ))}
        </div>
        <div className="funnel-chart-grid" aria-hidden="true">
          {ticks.map((t) => (
            <i key={t} style={{ bottom: `${(t / axisMax) * 100}%` }} />
          ))}
        </div>
        <div className="funnel-chart-cols" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
          {data.map((d, i) => {
            const h = (valueOf(d.users) / axisMax) * 100;
            const prevH = i === 0 ? h : (valueOf(data[i - 1].users) / axisMax) * 100;
            const dropH = Math.max(0, prevH - h);
            const evs = stepEvents(d);
            return (
              <div className="funnel-col" key={d.event}>
                <div className="funnel-col-track">
                  {dropH > 0 && (
                    <button
                      type="button"
                      className={`funnel-col-drop ${hover?.i === i && hover?.part === "drop" ? "is-hover" : ""}`}
                      style={{ bottom: `${h}%`, height: `${dropH}%` }}
                      aria-label={`${d.dropped} users dropped before ${labelizeKey(d.event)}`}
                      onMouseMove={(e) => showTip(e, i, "drop")}
                      onFocus={(e) => showTip(e, i, "drop")}
                      onClick={() => openAndClear(i - 1, "dropped")}
                    />
                  )}
                  <button
                    type="button"
                    className={`funnel-col-bar ${evs && d.users ? "funnel-col-bar--split" : ""} ${hover?.i === i && hover?.part === "bar" ? "is-hover" : ""}`}
                    style={{ height: `${Math.max(h, d.users ? 0.8 : 0)}%` }}
                    aria-label={`${d.users} users reached ${labelizeKey(d.event)}`}
                    onMouseMove={(e) => showTip(e, i, "bar")}
                    onFocus={(e) => showTip(e, i, "bar")}
                    onClick={() => d.users && openAndClear(i, "reached")}
                  >
                    {evs && d.users > 0 && evs.map((ev) => <span key={ev.event} style={{ height: `${(ev.users / d.users) * 100}%`, background: ev.color }} />)}
                  </button>
                </div>
                {i > 0 && (
                  <div className="funnel-col-conv" title={`${pct(d.pct_of_prev)} of ${labelizeKey(data[i - 1].event)} users moved on to ${labelizeKey(d.event)}`}>
                    <MdArrowForward />
                    <span>{pct(d.pct_of_prev)} moved on</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="funnel-chart-labels" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
        {data.map((d, i) => (
          <div key={d.event} className="funnel-chart-label">
            <small>Step {i + 1}</small>
            <strong title={labelizeKey(d.event)}>{labelizeKey(d.event)}</strong>
            <span className="funnel-label-val"><b>{countFormat(d.users)}</b> · {pct(d.pct_of_first)}</span>
            {d.amount != null && <span className="funnel-label-val"><b>{money(d.amount)}</b>{d.events != null ? ` (${countFormat(d.events)})` : ""}</span>}
            {stepEvents(d) && <StepEventList events={stepEvents(d)} />}
          </div>
        ))}
      </div>
      <div className="funnel-legend">
        <span><i className="funnel-legend-swatch funnel-legend-swatch--reached" /> Reached the step</span>
        <span><i className="funnel-legend-swatch funnel-legend-swatch--drop" /> Dropped off from the previous step</span>
      </div>

      {hover && data[hover.i] && (
        <div
          className="funnel-tip"
          style={{ left: Math.min(Math.max(hover.x + 14, 8), hover.w - 250), top: Math.max(hover.y - 20, 0) }}
        >
          {hover.part === "bar" ? (
            <>
              <div className="funnel-tip-title">Step {hover.i + 1} · {labelizeKey(data[hover.i].event)}</div>
              <div className="funnel-tip-row"><span>Users reached</span><strong>{countFormat(data[hover.i].users)}</strong></div>
              {stepEvents(data[hover.i])?.map((ev) => (
                <div key={ev.event} className="funnel-tip-row">
                  <span><i className="funnel-tip-dot" style={{ background: ev.color }} />{labelizeKey(ev.event)}</span>
                  <strong>{countFormat(ev.users)}</strong>
                </div>
              ))}
              {data[hover.i].events != null && (
                <div className="funnel-tip-row"><span>Events in range</span><strong>{countFormat(data[hover.i].events)}</strong></div>
              )}
              {data[hover.i].amount != null && (
                <div className="funnel-tip-row"><span>Amount</span><strong>{money(data[hover.i].amount)}{data[hover.i].events != null ? ` (${countFormat(data[hover.i].events)})` : ""}</strong></div>
              )}
              <div className="funnel-tip-row"><span>Of step 1 users</span><strong>{pct(data[hover.i].pct_of_first)}</strong></div>
              {hover.i > 0 && (
                <>
                  <div className="funnel-tip-row"><span>From previous step</span><strong>{pct(data[hover.i].pct_of_prev)}</strong></div>
                  {data[hover.i].avg_ms_from_prev != null && (
                    <div className="funnel-tip-row"><span>Avg time from previous</span><strong>{formatDuration(data[hover.i].avg_ms_from_prev)}</strong></div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="funnel-tip-title">Dropped after {labelizeKey(data[hover.i - 1]?.event)}</div>
              <div className="funnel-tip-row"><span>Users lost</span><strong>{countFormat(data[hover.i].dropped)}</strong></div>
              <div className="funnel-tip-row">
                <span>Of previous step</span>
                <strong>{pct(100 - (Number(data[hover.i].pct_of_prev) || 0))}</strong>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------ chart tabs -- */

/** Graphs from the Funnel Design Lab, one tab each (same applied data). */
const CHART_TABS = [
  { key: "funnel", label: "Funnel", title: "Conversion funnel", help: "Bar height = users who reached each step (or % of step 1). The hatched part is who dropped." },
  { key: "steps", label: "Steps", title: "Steps", help: "Each step as a bar of step-1 users, with where dropped users went next." },
  { key: "flow", label: "Flow map", title: "Flow map", help: "Blue bands carry users to the next step. Red bands show the first thing people did after leaving." },
  { key: "glance", label: "At a glance", title: "The funnel at a glance", help: "Width shows how many users are left at each step. Click a step or a drop note." },
  { key: "groups", label: "By group", title: "Each group side by side", help: "Bar height is the share of that group's starters who reached the step." },
  { key: "time", label: "Over time", title: "Conversion over time", help: "Bars: how many started and completed. Line: share who completed (right scale)." },
  { key: "cohorts", label: "Cohorts", title: "Cohort table", help: "Each row is a group of users who started together. Darker cells mean more of them reached that step." },
  { key: "convert", label: "Time to convert", title: "How long it takes", help: "Users who finished, grouped by how long they took from the first step to the last." },
];

// Monday of the week of a "YYYY-MM-DD" day, with the shared date helpers from utils.js.
const weekKey = (iso) => toLocalIsoDate(getWeekMonday(parseLocalIsoDate(iso)));
/** Day-level trend points → day / week / month buckets (sums of started, completed and reached per step). */
function groupTrend(daily, unit) {
  if (unit === "day") return daily;
  const map = new Map();
  daily.forEach((p) => {
    const key = unit === "week" ? weekKey(p.date) : `${p.date.slice(0, 7)}-01`;
    const b = map.get(key) || { date: key, started: 0, completed: 0, reached: p.reached.map(() => 0) };
    b.started += p.started;
    b.completed += p.completed;
    p.reached.forEach((n, i) => (b.reached[i] += n));
    map.set(key, b);
  });
  return [...map.values()].map((b) => ({ ...b, rate: b.started ? Number(((b.completed / b.started) * 100).toFixed(1)) : null }));
}

function ChartEmpty({ children }) {
  return <div className="funnel-chart-empty">{children}</div>;
}

/** Leak destinations of one step (top events, Other, No further activity), scaled to all dropped users. */
function leakParts(leak) {
  if (!leak) return [];
  const scale = leak.sampled ? leak.dropped / leak.sampled : 0;
  return [
    ...leak.next.map((n) => ({ label: labelizeKey(n.event), users: n.users })),
    ...(leak.other ? [{ label: "Other", users: leak.other }] : []),
    ...(leak.none ? [{ label: "No further activity", users: leak.none }] : []),
  ]
    .map((x) => ({ ...x, shown: Math.round(x.users * scale) }))
    .filter((x) => x.users > 0);
}

function StepsStack({ data, leaks, onOpen }) {
  return (
    <div className="funnel-stack">
      {data.map((d, i) => {
        const leak = leaks?.[i];
        const parts = leakParts(leak);
        const next = data[i + 1];
        return (
          <div key={d.event} className="funnel-stack-step">
            <div className="funnel-stack-head">
              <span className="funnel-table-step">
                <span className="funnel-step-num">{i + 1}</span>
                <strong>{labelizeKey(d.event)}</strong>
              </span>
              <span className="funnel-stack-users"><b>{countFormat(d.users)}</b> users · {pct(d.pct_of_first)}</span>
            </div>
            <button type="button" className="funnel-stack-track" onClick={() => d.users && onOpen(i, "reached")} aria-label={`${d.users} users reached ${labelizeKey(d.event)}`}>
              <i style={{ width: `${Math.max(d.pct_of_first, d.users ? 1 : 0)}%` }}>{pct(d.pct_of_first)}</i>
            </button>
            {next && (
              <div className="funnel-stack-drop">
                <span className="funnel-stack-pill">→ {pct(next.pct_of_prev)} continued</span>
                <button type="button" className="funnel-stack-pill funnel-stack-pill--bad" disabled={!next.dropped} onClick={() => onOpen(i, "dropped")}>
                  {countFormat(next.dropped)} dropped
                </button>
                {next.median_ms_from_prev != null && <span className="funnel-stack-note">median {formatDuration(next.median_ms_from_prev)}</span>}
                {next.dropped > 0 && (
                  <span className="funnel-stack-next">
                    <span className="funnel-stack-note">
                      Dropped users went to{leak?.sampled && leak.sampled < leak.dropped ? ` (based on the ${countFormat(leak.sampled)} newest of ${countFormat(leak.dropped)})` : ""}:
                    </span>
                    {leaks == null ? (
                      <span className="funnel-stack-note">loading…</span>
                    ) : (
                      parts.map((p) => (
                        <span key={p.label} className="funnel-chip">
                          {p.label} <span className="funnel-chip-key">{next.dropped ? Math.round((p.shown / next.dropped) * 100) : 0}%</span>
                        </span>
                      ))
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Sankey-like map: blue bands step → step, red bands from each step to what dropped users did next. */
function FlowMap({ data, leaks, onOpen }) {
  const first = data[0]?.users || 0;
  if (!first) return <ChartEmpty>No users in this funnel yet.</ChartEmpty>;
  const W = 1000;
  const colW = 12;
  const top = 46;
  const H = 240;
  const n = data.length;
  const gapX = (W - 40 - colW - 150) / Math.max(1, n - 1);
  const x = (i) => 20 + i * gapX;
  const h = (users) => Math.max(users ? 2 : 0, (users / first) * H);
  const band = (x0, y0a, y0b, x1, y1a, y1b) => {
    const mx = (x0 + x1) / 2;
    return `M${x0},${y0a} C${mx},${y0a} ${mx},${y1a} ${x1},${y1a} L${x1},${y1b} C${mx},${y1b} ${mx},${y0b} ${x0},${y0b} Z`;
  };
  let maxY = top + H;
  const leakShapes = [];
  data.forEach((d, i) => {
    if (i === n - 1) return;
    const parts = leakParts(leaks?.[i]);
    const next = data[i + 1];
    if (!next.dropped || !parts.length) return;
    const srcTop = top + h(next.users);
    const srcH = h(d.users) - h(next.users);
    const total = parts.reduce((a, p) => a + p.shown, 0) || 1;
    let sy = srcTop;
    let dy = top + H + 40;
    const dx = x(i) + colW + Math.min(120, gapX * 0.45);
    parts.forEach((p, k) => {
      const ph = (p.shown / total) * srcH;
      const dh = Math.max(3, ph);
      leakShapes.push({ key: `${i}-${k}`, i, path: band(x(i) + colW, sy, sy + ph, dx, dy, dy + dh), dx, dy, dh, label: p.label, users: p.shown });
      sy += ph;
      dy += dh + 14;
    });
    maxY = Math.max(maxY, dy);
  });
  return (
    <div className="funnel-flow">
      <svg viewBox={`0 0 ${W} ${maxY + 10}`} role="img" aria-label="Flow of users through the funnel steps">
        {data.map((d, i) => (
          <g key={d.event}>
            <text x={x(i)} y={16} className="funnel-flow-title">{i + 1}. {labelizeKey(d.event)}</text>
            <text x={x(i)} y={32} className="funnel-flow-sub">{countFormat(d.users)} · {pct(d.pct_of_first)} of start</text>
            {i < n - 1 && data[i + 1].users > 0 && (
              <g className="funnel-flow-band" onClick={() => onOpen(i + 1, "reached")}>
                <path d={band(x(i) + colW, top, top + h(data[i + 1].users), x(i + 1), top, top + h(data[i + 1].users))} />
                <text x={(x(i) + x(i + 1) + colW) / 2} y={top + 16} textAnchor="middle">{pct(data[i + 1].pct_of_prev)}</text>
              </g>
            )}
            <rect x={x(i)} y={top} width={colW} height={h(d.users)} rx="2" className="funnel-flow-node" onClick={() => d.users && onOpen(i, "reached")} />
          </g>
        ))}
        {leakShapes.map((l) => (
          <g key={l.key} className="funnel-flow-leak" onClick={() => onOpen(l.i, "dropped")}>
            <path d={l.path} />
            <rect x={l.dx} y={l.dy} width="5" height={l.dh} />
            <text x={l.dx + 10} y={l.dy + Math.min(l.dh, 14) - 2}>
              <tspan className="funnel-flow-leak-name">{l.label.length > 22 ? `${l.label.slice(0, 21)}…` : l.label}</tspan> · {countFormat(l.users)}
            </text>
          </g>
        ))}
      </svg>
      {leaks == null && <div className="funnel-stack-note">Loading where users went after leaving…</div>}
    </div>
  );
}

function BiggestLeaks({ data, leaks, onOpen }) {
  const list = (leaks || [])
    .flatMap((l) =>
      leakParts(l).map((p) => ({ ...p, i: l.after_step, from: labelizeKey(data[l.after_step]?.event), to: labelizeKey(data[l.after_step + 1]?.event) }))
    )
    .sort((a, b) => b.shown - a.shown)
    .slice(0, 7);
  return (
    <div className="funnel-leaks">
      <h3>Biggest leaks</h3>
      <p>
        Largest groups of users who left, by what they did next.
        {(leaks || []).some((l) => l.sampled < l.dropped) ? " Big steps are estimated from their newest dropped users." : ""}
      </p>
      {leaks == null ? (
        <div className="funnel-stack-note">Loading…</div>
      ) : list.length === 0 ? (
        <div className="funnel-stack-note">No drop-offs in this funnel.</div>
      ) : (
        list.map((l) => (
          <button key={`${l.i}-${l.label}`} type="button" className="funnel-leak" onClick={() => onOpen(l.i, "dropped")}>
            <b>{countFormat(l.shown)}</b>
            <span>
              <strong>{l.label}</strong>
              <small>after {l.from}, instead of {l.to}</small>
            </span>
          </button>
        ))
      )}
    </div>
  );
}

function FunnelGlance({ data, onOpen }) {
  const first = data[0]?.users || 0;
  if (!first) return <ChartEmpty>No users in this funnel yet.</ChartEmpty>;
  const w = (users) => Math.max(8, (users / first) * 100);
  return (
    <div className="funnel-glance">
      {data.map((d, i) => {
        const a = w(d.users);
        const b = i < data.length - 1 ? w(data[i + 1].users) : a;
        const next = data[i + 1];
        const shade = 1 - (i / Math.max(1, data.length)) * 0.45;
        return (
          <React.Fragment key={d.event}>
            <div className="funnel-glance-row">
              <div className="funnel-glance-name"><small>Step {i + 1}</small><strong>{labelizeKey(d.event)}</strong></div>
              <button
                type="button"
                className="funnel-glance-shape"
                aria-label={`${d.users} users reached ${labelizeKey(d.event)}`}
                onClick={() => d.users && onOpen(i, "reached")}
                style={{
                  clipPath: `polygon(${(100 - a) / 2}% 0, ${(100 + a) / 2}% 0, ${(100 + b) / 2}% 100%, ${(100 - b) / 2}% 100%)`,
                  opacity: shade,
                }}
              />
              <div className="funnel-glance-num"><strong>{countFormat(d.users)}</strong><small>{pct(d.pct_of_first)} of start</small></div>
            </div>
            {next && (
              <button type="button" className="funnel-glance-drop" disabled={!next.dropped} onClick={() => onOpen(i, "dropped")}>
                ▼ {countFormat(next.dropped)} left ({pct(100 - (Number(next.pct_of_prev) || 0))}) · {pct(next.pct_of_prev)} continued
              </button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function GroupBars({ breakdown, data }) {
  const [hidden, setHidden] = useState({});
  if (!breakdown?.groups?.length) return <ChartEmpty>Choose <strong>Breakdown by</strong> (Platform, Source, Campaign, Country, State or City) and click <strong>Show funnel</strong> to compare groups here.</ChartEmpty>;
  const groups = breakdown.groups.slice(0, FUNNEL_EVENT_COLORS.length);
  const rows = data.map((d, i) => ({
    name: `${i + 1}. ${labelizeKey(d.event)}`,
    ...Object.fromEntries(groups.map((g) => [g.label, g.pct_of_first[i]])),
  }));
  return (
    <>
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
            <Tooltip formatter={(v) => `${v}%`} />
            {groups.map((g, k) => !hidden[g.label] && <Bar key={g.label} dataKey={g.label} fill={FUNNEL_EVENT_COLORS[k]} radius={[4, 4, 0, 0]} maxBarSize={36} />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="funnel-legend">
        {groups.map((g, k) => (
          <button key={g.label} type="button" className={`funnel-legend-toggle ${hidden[g.label] ? "is-off" : ""}`} onClick={() => setHidden((h) => ({ ...h, [g.label]: !h[g.label] }))}>
            <i style={{ background: FUNNEL_EVENT_COLORS[k] }} /> {g.label}
          </button>
        ))}
        <span>Click a name to hide or show it</span>
      </div>
    </>
  );
}

function TrendTab({ trend, steps }) {
  const [unit, setUnit] = useState(trend?.unit || "day");
  const daily = trend?.daily || [];
  if (daily.length < 2) return <ChartEmpty>Not enough days in this range to show a trend.</ChartEmpty>;
  const points = groupTrend(daily, unit);
  return (
    <>
      <div className="funnel-chart-toolbar">
        <div className="funnel-seg funnel-seg--sm">
          {["day", "week", "month"].map((u) => (
            <button key={u} type="button" className={unit === u ? "active" : ""} onClick={() => setUnit(u)}>{u[0].toUpperCase() + u.slice(1)}</button>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => trendLabel(d, unit)} />
            <YAxis yAxisId="n" allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
            <YAxis yAxisId="p" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
            <Tooltip
              formatter={(v, n) => (n === "Conversion" ? (v == null ? "—" : `${v}%`) : v)}
              labelFormatter={(d) => `${unit === "week" ? "Week of " : ""}${trendLabel(d, unit, true)}`}
            />
            <Bar yAxisId="n" dataKey="started" name={`Started (${labelizeKey(steps[0])})`} fill="#c7cbf5" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="n" dataKey="completed" name={`Completed (${labelizeKey(steps[steps.length - 1])})`} fill="#4545c4" radius={[4, 4, 0, 0]} />
            <Line yAxisId="p" type="monotone" dataKey="rate" name="Conversion" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function CohortTab({ trend, steps }) {
  const [unit, setUnit] = useState("month");
  const rows = groupTrend(trend?.daily || [], unit).filter((r) => r.started > 0);
  if (!rows.length) return <ChartEmpty>No users in this range.</ChartEmpty>;
  return (
    <>
      <div className="funnel-chart-toolbar">
        <div className="funnel-seg funnel-seg--sm">
          {["week", "month"].map((u) => (
            <button key={u} type="button" className={unit === u ? "active" : ""} onClick={() => setUnit(u)}>{u === "week" ? "Weekly" : "Monthly"}</button>
          ))}
        </div>
      </div>
      <div className="table-responsive">
        <table className="funnel-cohort">
          <thead>
            <tr>
              <th>Started in</th>
              <th className="text-end">Users</th>
              {steps.map((s, i) => <th key={s}>{i + 1}. {labelizeKey(s)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td><strong>{unit === "week" ? `Week of ${trendLabel(r.date, "week", true)}` : trendLabel(r.date, "month")}</strong></td>
                <td className="text-end">{countFormat(r.started)}</td>
                {r.reached.map((n, i) => {
                  const p = r.started ? n / r.started : 0;
                  return (
                    <td key={i} title={`${countFormat(n)} of ${countFormat(r.started)} users`}>
                      <span className="funnel-cohort-cell" style={{ background: `rgba(67, 70, 206, ${0.12 + p * 0.88})`, color: p > 0.5 ? "#fff" : "#1a2332" }}>
                        {Math.round(p * 100)}%
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ConvertTab({ ttc, data }) {
  const buckets = ttc?.buckets || [];
  if (!buckets.some((b) => b.users)) return <ChartEmpty>Nobody finished the funnel in this range yet.</ChartEmpty>;
  const gaps = data.slice(1).map((d, i) => ({ label: `${labelizeKey(data[i].event)} → ${labelizeKey(d.event)}`, ms: d.median_ms_from_prev || 0 }));
  const gapTotal = gaps.reduce((a, g) => a + g.ms, 0);
  return (
    <>
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={buckets} margin={{ top: 20, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
            <Tooltip formatter={(v) => [`${countFormat(v)} users`, "Finished"]} />
            <Bar dataKey="users" name="Users" fill="#4545c4" radius={[4, 4, 0, 0]}>
              <LabelList dataKey="users" position="top" style={{ fontSize: 11, fill: "#6b7785" }} formatter={(v) => (v ? countFormat(v) : "")} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="funnel-convert-grid">
        <div>
          <h3>Converted by day X</h3>
          <p>Share of users who started and finished within that many days.</p>
          <div style={{ width: "100%", height: 180 }}>
            <ResponsiveContainer>
              <ComposedChart data={ttc.by_day} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => `Day ${d}`} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
                <Tooltip formatter={(v) => `${v}%`} labelFormatter={(d) => `Within ${d} day${d === 1 ? "" : "s"}`} />
                <Line type="monotone" dataKey="pct" name="Finished" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h3>Where the time goes</h3>
          <p>Median time between each pair of steps.</p>
          {gapTotal > 0 && (
            <div className="funnel-timebar">
              {gaps.map((g, k) => g.ms > 0 && (
                <i key={g.label} style={{ width: `${(g.ms / gapTotal) * 100}%`, background: FUNNEL_EVENT_COLORS[k % FUNNEL_EVENT_COLORS.length] }} title={`${g.label}: ${formatDuration(g.ms)}`} />
              ))}
            </div>
          )}
          {gaps.map((g, k) => (
            <div key={g.label} className="funnel-timerow">
              <span><i style={{ background: FUNNEL_EVENT_COLORS[k % FUNNEL_EVENT_COLORS.length] }} />{g.label}</span>
              <strong>{g.ms ? formatDuration(g.ms) : "—"}</strong>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function FunnelChartTabs({ result, steps, leaks, onNeedLeaks, onOpen }) {
  const [tab, setTab] = useState("funnel");
  const data = result.data || [];
  const meta = CHART_TABS.find((t) => t.key === tab);
  useEffect(() => {
    if (tab === "steps" || tab === "flow") onNeedLeaks();
  }, [tab, onNeedLeaks]);
  return (
    <div className="funnel-card">
      <div className="funnel-card-head funnel-card-head--tabs">
        <div>
          <h2>{meta.title}</h2>
          <p>{meta.help}</p>
        </div>
        <div className="funnel-seg funnel-chart-tabs" role="tablist" aria-label="Graph">
          {CHART_TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "funnel" && <FunnelChart data={data} onOpen={onOpen} />}
      {tab === "steps" && <StepsStack data={data} leaks={leaks} onOpen={onOpen} />}
      {tab === "flow" && (
        <div className="funnel-flow-layout">
          <FlowMap data={data} leaks={leaks} onOpen={onOpen} />
          <BiggestLeaks data={data} leaks={leaks} onOpen={onOpen} />
        </div>
      )}
      {tab === "glance" && <FunnelGlance data={data} onOpen={onOpen} />}
      {tab === "groups" && <GroupBars breakdown={result.breakdown} data={data} />}
      {tab === "time" && <TrendTab trend={result.trend} steps={steps} />}
      {tab === "cohorts" && <CohortTab trend={result.trend} steps={steps} />}
      {tab === "convert" && <ConvertTab ttc={result.time_to_convert} data={data} />}
    </div>
  );
}

/* ---------------------------------------------------------- users modal -- */

function FunnelUsersModal({ mode, show, onHide, stepIndex, kind, group, steps, data, rows, meta, loading, error, onRetry, page, onPage, onExport, exporting, onCreateSegment }) {
  const stepName = labelizeKey(steps[stepIndex]);
  const nextName = labelizeKey(steps[stepIndex + 1]);
  const amountStep = data[stepIndex]?.amount != null; // Event Metric step that shows an amount

  return (
    <Modal show={show} onHide={onHide} size="xl" className="ns-modal" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {kind === "dropped" ? `Dropped before ${nextName} · Step ${stepIndex + 2}` : `Step ${stepIndex + 1} · ${stepName}`}{group ? ` · ${group}` : ""}
          <small className="funnel-users-sub">
            {kind === "all"
              ? `Everyone who did ${labelizeKey(steps[0])}, with how far each one got`
              : kind === "reached"
                ? `Users who did ${stepName}${stepIndex > 0 ? (mode === "any" ? " (in any order)" : " after the previous steps") : ""}`
                : `Users who did ${stepName} but never did ${nextName}`}
          </small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {kind === "dropped" && meta?.next_summary?.length > 0 && (
          <div className="funnel-next-summary">
            <span>After dropping, these users also did:</span>
            {meta.next_summary.map((n) => (
              <span key={n.event} className="badge rounded-pill bg-light-secondary">
                {labelizeKey(n.event)} · {plural(n.users, "user")}
              </span>
            ))}
          </div>
        )}
        {loading ? (
          <SkeletonLoader columns={[1, 2, 3, 4, 5, 6]} rows={5} />
        ) : error ? (
          <div className="funnel-empty funnel-empty--sm" role="alert">
            <MdPeopleOutline size={34} />
            <p>{error}</p>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onRetry}>
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="funnel-empty funnel-empty--sm">
            <MdPeopleOutline size={34} />
            <p>No users here for the selected filters.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table funnel-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Platform</th>
                  <th>Progress</th>
                  <th>{stepName} at</th>
                  {amountStep && <th className="text-end">{stepName} amount</th>}
                  <th>Last step reached</th>
                  <th>Path (event by event)</th>
                  {kind === "dropped" && <th>Did instead</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const display = u.name || (u.is_anonymous ? "Anonymous user" : `User #${u.app_user_id}`);
                  return (
                    <tr key={u.key}>
                      <td>
                        <div className="funnel-user">
                          <span className={`funnel-avatar ${u.is_anonymous ? "funnel-avatar--anon" : ""}`}>{u.is_anonymous ? "?" : initials(display)}</span>
                          <div>
                            <div className="funnel-user-name">{display}</div>
                            <small className="text-muted">
                              {u.is_anonymous ? (u.device ? `Not logged in · ${u.device}` : "Not logged in yet") : `ID ${u.app_user_id}`}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          {u.phone && <span>{u.phone}</span>}
                          {u.email && <small className="text-muted">{u.email}</small>}
                          {!u.phone && !u.email && <small className="text-muted">—</small>}
                        </div>
                      </td>
                      <td>
                        {u.platforms?.length ? (
                          u.platforms.map((p) => <span key={p} className="badge rounded-pill bg-light-secondary me-1">{p}</span>)
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        <div
                          className="funnel-dots"
                          title={steps.map((s, i) => `${labelizeKey(s)}: ${u.step_times[i] ? formatDateTime(u.step_times[i]) : "not yet"}`).join("\n")}
                        >
                          {steps.map((s, i) => (
                            <i key={s} className={i < u.reached_steps ? "on" : ""} />
                          ))}
                          <small>{u.reached_steps}/{steps.length}</small>
                        </div>
                      </td>
                      <td>
                        {u.step_times[stepIndex] ? (
                          <small>{formatDateTime(u.step_times[stepIndex])}</small>
                        ) : kind === "all" ? (
                          <span className="badge rounded-pill bg-light-secondary">Not reached</span>
                        ) : (
                          <small>—</small>
                        )}
                      </td>
                      {amountStep && <td className="text-end">{u.step_times[stepIndex] ? `${money(u.step_amounts?.[stepIndex] ?? 0)}${u.step_counts?.[stepIndex] != null ? ` (${u.step_counts[stepIndex]})` : ""}` : "—"}</td>}
                      <td><small>{labelizeKey(steps[u.reached_steps - 1])}</small></td>
                      <td>
                        {u.path?.length ? (
                          <div className="funnel-path">
                            {u.path.map((n, k) => (
                              <React.Fragment key={`${n.event}-${k}`}>
                                {k > 0 && <span className="funnel-path-arrow">→</span>}
                                <span
                                  className={`funnel-path-chip ${n.step ? "funnel-path-chip--step" : ""}`}
                                  title={formatDateTime(n.at)}
                                >
                                  {labelizeKey(n.event)}{n.count > 1 ? ` ×${n.count}` : ""}
                                </span>
                              </React.Fragment>
                            ))}
                            {u.path_total > u.path.length && <span className="funnel-path-more">+{u.path_total - u.path.length} more</span>}
                          </div>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      {kind === "dropped" && (
                        <td>
                          {u.next_events?.length ? (
                            <div className="d-flex flex-column">
                              <span>
                                {u.next_events.map((n) => `${labelizeKey(n.event)}${n.count > 1 ? ` ×${n.count}` : ""}`).join(", ")}
                              </span>
                              {u.last_activity_at && <small className="text-muted">Last active {formatDateTime(u.last_activity_at)}</small>}
                            </div>
                          ) : (
                            <small className="text-muted">No activity since</small>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="funnel-users-footer">
        <div className="flex-grow-1">
          {meta?.total ? (
            <ListPaginationBar
              page={page}
              setPage={onPage}
              total={meta.total}
              pageSize={FUNNEL_USERS_PAGE_SIZE}
              totalPages={meta.totalPages}
              ariaLabel="Funnel users pagination"
              loading={loading}
              className="p-0"
            />
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-2">
          {onCreateSegment && (
            <button type="button" className="btn_theme_secondary" onClick={onCreateSegment} disabled={!meta?.total}>
              <MdSend /> Message these users
            </button>
          )}
          <button type="button" className="btn_theme_secondary" onClick={onExport} disabled={exporting || !meta?.total}>
            <MdFileDownload /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="btn_theme_primary" onClick={onHide}>Close</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/* ----------------------------------------------------------------- page -- */

export default function FunnelAnalysis() {
  const { appId: routeAppId, funnelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !funnelId;
  const canManage = can("funnel.manage");
  const [appUuid, setAppUuid] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [savedKey, setSavedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [funnelLoading, setFunnelLoading] = useState(!isNew);
  const [funnelMissing, setFunnelMissing] = useState(false);
  const [steps, setSteps] = useState(["", ""]);
  const [mode, setMode] = useState("ordered");
  const [windowDays, setWindowDays] = useState("");
  const [filters, setFilters] = useState(getDefaultFilters);
  // Open popover in the command bar: "filters" or "step-<index>"; null when none.
  const [pop, setPop] = useState(null);
  const [popTab, setPopTab] = useState("audience");
  const togglePop = (key) => setPop((cur) => (cur === key ? null : key));
  // Close the open popover on a click outside it (or Esc). The click itself still reaches what was clicked,
  // so opening the next step / pressing Save works in one click (a backdrop used to swallow it).
  useEffect(() => {
    if (!pop) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (t?.closest?.(".funnel-pop-anchor") || t?.closest?.('[class*="__menu"]')) return; // inside it, or its dropdown list (portal)
      setPop(null);
    };
    const onKey = (e) => e.key === "Escape" && setPop(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pop]);
  const [missingStep, setMissingStep] = useState(-1); // empty step the last Show funnel / Save stopped at
  // New funnel: name + description are asked in a popup when it is saved.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // "Where users went after leaving" (Steps / Flow map tabs), loaded only when one of those tabs opens.
  const [leaks, setLeaks] = useState({ key: "", data: null });
  // Names of the selected campaigns (id -> name) for the summary chip; the filter itself keeps only ids.
  const [campaignNames, setCampaignNames] = useState({});

  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  const stepTableRef = useRef(null);
  const stepTableInnerRef = useRef(null);
  const breakdownTableRef = useRef(null);
  const breakdownTableInnerRef = useRef(null);
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef: stepTableRef,
    optionCount: 2,
    menuWidth: 200,
    useAnchored: true,
  });
  const [usersModal, setUsersModal] = useState({ show: false, stepIndex: 0, kind: "reached" });
  const [usersData, setUsersData] = useState({ rows: [], meta: {} });
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null); // shown in the users modal with a Try again button
  const usersReq = useRef(0); // only the latest users request may update the modal
  const [exporting, setExporting] = useState(false);
  const loadedFor = useRef(null);
  // "Message these users": turns the users of the open list into a Segment, then offers where to use it.
  const [segmentModal, setSegmentModal] = useState({ show: false, name: "", saving: false, created: null });

  const fetchOpts = useCallback(
    (type, extra = {}) =>
      ({ search, ids }) =>
        fetchAjaxListData({ type, q: search, id: ids, extra: { app_id: appUuid, ...extra }, mode: "options" }),
    [appUuid]
  );
  // with_metrics: also list the app's Event Metrics (Booking, Revenue…) — a step can be one of them.
  const fetchEventOpts = useMemo(() => fetchOpts("campaign-event", { with_metrics: 1 }), [fetchOpts]);
  const fetchCampaignOpts = useMemo(() => fetchOpts("campaigns"), [fetchOpts]);
  const fetchCountryOpts = useMemo(() => fetchOpts("campaign-country"), [fetchOpts]);
  // Events already used in another step are not offered again (each step must be different).
  const stepOptsFor = useCallback(
    (i) => async (args) => {
      const res = await fetchEventOpts(args);
      if (args?.ids?.length) return res;
      const taken = new Set(steps.filter((x, k) => k !== i && x).flatMap(splitStep));
      return Array.isArray(res) ? res.filter((o) => !taken.has(String(o?.value))) : res;
    },
    [fetchEventOpts, steps]
  );
  // Exclusion list: plain events only (not Event Metrics), and never one that is already a funnel step.
  const fetchExcludeOpts = useCallback(
    async (args) => {
      const res = await fetchEventOpts(args);
      if (args?.ids?.length) return res;
      const taken = new Set(steps.filter(Boolean).flatMap(splitStep));
      return Array.isArray(res) ? res.filter((o) => !String(o?.value).startsWith("metric:") && !taken.has(String(o?.value))) : res;
    },
    [fetchEventOpts, steps]
  );
  const fetchPlatformOpts = useMemo(() => fetchOpts("subscription-platform"), [fetchOpts]);
  const fetchStateOpts = useMemo(() => fetchOpts("app-user-state"), [fetchOpts]);
  const fetchCityOpts = useMemo(() => fetchOpts("app-user-city"), [fetchOpts]);

  const cleanSteps = steps.map((s) => String(s || "").trim()).filter(Boolean);
  const allParts = cleanSteps.flatMap(splitStep);
  const duplicate = new Set(allParts).size !== allParts.length;
  // an event already used in another step marks that step as a duplicate
  const isDupStep = (s, i) => splitStep(s).some((p) => steps.some((x, k) => k !== i && splitStep(x).includes(p)));
  const canRun = cleanSteps.length >= FUNNEL_MIN_STEPS && cleanSteps.length === steps.length && !duplicate;
  const emptyStep = steps.findIndex((x) => !String(x || "").trim());
  // Why the funnel cannot run yet; also opens the empty step so it is easy to fix.
  const stepProblem = () => {
    if (duplicate) return "Each step must be a different event";
    if (emptyStep >= 0) {
      setMissingStep(emptyStep);
      setPop(`step-${emptyStep}`);
      return `Step ${emptyStep + 1} has no event yet. Pick an event for it or remove that step.`;
    }
    return `Add at least ${FUNNEL_MIN_STEPS} steps`;
  };

  const runWith = useCallback(
    async (st, md, wd, f, { silent = false, fresh = false } = {}) => {
      if (!appUuid) return;
      const body = {
        app_id: appUuid,
        steps: st,
        mode: md,
        window_days: wd || undefined,
        campaign_id: f.campaign_id || undefined,
        is_organic: f.is_organic === "" ? undefined : f.is_organic,
        country: f.country || undefined,
        platform: f.platform || undefined,
        state: f.state || undefined,
        city: f.city || undefined,
        breakdown: f.breakdown || undefined,
        exclude_events: f.exclude_events || undefined,
        ...(f.dateFrom && f.dateTo
          ? { date_from: f.dateFrom, date_to: f.dateTo, timezone: getBrowserTimeZone() || undefined }
          : {}),
      };
      setLoading(true);
      let res;
      let json;
      try {
        res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/campaign/funnel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Refresh asks the server to skip its short cache; the stored body stays without it
          body: JSON.stringify(fresh ? { ...body, fresh: 1 } : body),
        });
        json = await res.json().catch(() => ({}));
      } catch {
        if (!silent) toast.error("Could not reach the server. Please try again.");
        return;
      } finally {
        setLoading(false);
      }
      if (handleSessionExpired(res, json)) return;
      if (!json.status) {
        if (!silent) {
          const timedOut = res.status === 504 || res.status === 503 || res.status === 502;
          toast.error(
            json.message ||
              (timedOut
                ? "The server took too long to build this funnel. Try a shorter date range or add a filter."
                : "Failed to load funnel")
          );
        }
        return;
      }
      setResult(json);
      setApplied({ body, filters: f, mode: md, windowDays: wd });
    },
    [appUuid]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      const uuid = await fetchApplicationAppId(routeAppId);
      if (alive && uuid) setAppUuid(uuid);
    })();
    return () => {
      alive = false;
    };
  }, [routeAppId]);

  // View page: load the saved funnel, apply its saved filters and build the report straight away.
  useEffect(() => {
    if (!appUuid) return;
    const key = `${appUuid}:${funnelId || "new"}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    if (isNew) {
      setFunnelLoading(false);
      setFunnelMissing(false);
      setName("");
      setDescription("");
      setSteps(["", ""]);
      setMode("ordered");
      setWindowDays("");
      setFilters(getDefaultFilters());
      setResult(null);
      setApplied(null);
      setSavedKey(null);
      return;
    }
    (async () => {
      setFunnelLoading(true);
      setFunnelMissing(false);
      const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/funnel/detail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: appUuid, id: funnelId }),
      });
      const json = await res.json().catch(() => ({}));
      setFunnelLoading(false);
      if (handleSessionExpired(res, json)) return;
      if (!json.status || !json.data) {
        setFunnelMissing(true);
        toast.error(json.message || "Funnel not found");
        return;
      }
      const d = json.data;
      const st = Array.isArray(d.steps) && d.steps.length >= FUNNEL_MIN_STEPS ? d.steps : ["", ""];
      const md = FUNNEL_MODE_CODES.some((m) => m.key === d.mode) ? d.mode : "ordered";
      const wd = d.window_days ? String(d.window_days) : "";
      const f = savedToFilters(d.filters || {});
      setName(d.name || "");
      setDescription(d.description || "");
      setSteps(st);
      setMode(md);
      setWindowDays(wd);
      setFilters(f);
      setSavedKey(configKey({ name: d.name, description: d.description, steps: st, mode: md, windowDays: wd, filters: f }));
      runWith(st, md, wd, f, { silent: true });
    })();
  }, [appUuid, funnelId, isNew, runWith]);

  const campaignIds = useMemo(() => String(filters.campaign_id || "").split(",").filter(Boolean), [filters.campaign_id]);
  // Saved funnel opened with a campaign filter: look up names we do not have yet, once.
  useEffect(() => {
    const missing = campaignIds.filter((id) => campaignNames[id] == null);
    if (!missing.length || !appUuid) return;
    let alive = true;
    fetchCampaignOpts({ search: "", ids: missing }).then((res) => {
      if (!alive) return;
      const found = Object.fromEntries(missing.map((id) => [id, (Array.isArray(res) && res.find((o) => String(o.value) === id)?.label) || `Campaign #${id}`]));
      setCampaignNames((m) => ({ ...m, ...found }));
    });
    return () => {
      alive = false;
    };
  }, [campaignIds, campaignNames, appUuid, fetchCampaignOpts]);

  const currentKey = configKey({ name, description, steps, mode, windowDays, filters });
  const isDirty = isNew ? Boolean(name.trim() || cleanSteps.length) : savedKey != null && currentKey !== savedKey;

  const goBack = () => {
    const qs = location.state?.listSearch;
    navigate(`/apps/${routeAppId}/engage/funnel${qs ? `?${qs}` : ""}`);
  };

  const saveFunnel = async (funnelName = name, funnelDescription = description) => {
    if (!canManage || saving) return;
    if (!canRun) {
      toast.error(stepProblem());
      return;
    }
    if (!funnelName.trim()) {
      setDetailsOpen(true);
      return;
    }
    setSaving(true);
    const res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}save/funnel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        app_id: appUuid,
        id: isNew ? undefined : funnelId,
        name: funnelName.trim(),
        description: funnelDescription.trim(),
        steps: cleanSteps,
        mode,
        window_days: windowDays || null,
        filters: filtersToSaved(filters),
      }),
    });
    const json = await res.json().catch(() => ({}));
    setSaving(false);
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not save the funnel");
      return;
    }
    toast.success(json.message || "Funnel saved");
    setName(funnelName.trim());
    setDescription(funnelDescription.trim());
    setDetailsOpen(false);
    setSavedKey(configKey({ name: funnelName, description: funnelDescription, steps, mode, windowDays, filters }));
    // show the report for exactly what was saved
    runWith(cleanSteps, mode, windowDays, filters, { silent: true });
    if (isNew && json.data?.id) {
      loadedFor.current = `${appUuid}:${json.data.id}`;
      navigate(`/apps/${routeAppId}/engage/funnel/${json.data.id}`, { replace: true, state: location.state });
    }
  };

  const runFunnel = () => {
    if (!canRun) {
      toast.error(stepProblem());
      return;
    }
    runWith(cleanSteps, mode, windowDays, filters);
  };

  const leaksKey = applied ? JSON.stringify(applied.body) : "";
  const loadLeaks = useCallback(async () => {
    if (!applied || leaks.key === leaksKey) return;
    setLeaks({ key: leaksKey, data: null });
    let res;
    let json;
    try {
      res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/campaign/funnel/leaks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(applied.body),
      });
      json = await res.json().catch(() => ({}));
    } catch {
      toast.error("Could not reach the server. Please try again.");
      return;
    }
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not load where users went");
      return;
    }
    setLeaks({ key: leaksKey, data: json.data || [] });
  }, [applied, leaks.key, leaksKey]);

  const onRefresh = () => {
    if (!applied) return;
    setLeaks({ key: "", data: null }); // "where users went" is loaded again for the fresh numbers
    runWith(applied.body.steps, applied.mode, applied.windowDays, applied.filters, { fresh: true });
  };

  const loadUsers = useCallback(
    async (stepIndex, kind, page, group = "") => {
      if (!applied) return;
      const reqId = ++usersReq.current;
      setUsersLoading(true);
      setUsersError(null);
      let res;
      let json;
      try {
        res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}get/campaign/funnel/users`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...applied.body, step_index: stepIndex, kind, page, pageSize: FUNNEL_USERS_PAGE_SIZE, ...(group ? { group } : {}) }),
        });
        json = await res.json().catch(() => ({}));
      } catch {
        if (reqId === usersReq.current) setUsersError("The users could not be loaded: the server took too long or could not be reached.");
        return;
      } finally {
        if (reqId === usersReq.current) setUsersLoading(false);
      }
      if (reqId !== usersReq.current) return; // a newer list was opened meanwhile
      if (handleSessionExpired(res, json)) return;
      if (!json.status) {
        setUsersError(json.message || "The users could not be loaded.");
        return;
      }
      setUsersData({ rows: json.data || [], meta: json.meta || {} });
    },
    [applied]
  );

  const openUsers = (stepIndex, kind, group = "") => {
    if (stepIndex < 0) return;
    setUsersModal({ show: true, stepIndex, kind, group });
    setUsersPage(1);
    setUsersData({ rows: [], meta: {} });
    loadUsers(stepIndex, kind, 1, group);
  };
  const openSegmentModal = () => {
    const stepName = labelizeKey(applied?.body?.steps?.[usersModal.stepIndex]);
    const nextName = labelizeKey(applied?.body?.steps?.[usersModal.stepIndex + 1]);
    const base = name.trim() || "Funnel";
    setSegmentModal({
      show: true,
      saving: false,
      created: null,
      name: `${usersModal.kind === "dropped" ? `${base} · Dropped before ${nextName}` : usersModal.kind === "all" ? `${base} · All who started` : `${base} · Reached ${stepName}`}${usersModal.group ? ` · ${usersModal.group}` : ""}`,
    });
  };
  const createSegment = async () => {
    if (!applied || !segmentModal.name.trim()) {
      toast.error("Segment name is required");
      return;
    }
    setSegmentModal((m) => ({ ...m, saving: true }));
    let res;
    let json;
    try {
      res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}create/campaign/funnel/segment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...applied.body, step_index: usersModal.stepIndex, kind: usersModal.kind, ...(usersModal.group ? { group: usersModal.group } : {}), name: segmentModal.name.trim() }),
      });
      json = await res.json().catch(() => ({}));
    } catch {
      toast.error("Could not reach the server. Please try again.");
      setSegmentModal((m) => ({ ...m, saving: false }));
      return;
    }
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not create the segment");
      setSegmentModal((m) => ({ ...m, saving: false }));
      return;
    }
    setSegmentModal((m) => ({ ...m, saving: false, created: json.data }));
  };
  const goUseSegment = (path, state) => {
    setSegmentModal((m) => ({ ...m, show: false }));
    setUsersModal((m) => ({ ...m, show: false }));
    navigate(`/apps/${routeAppId}/${path}`, state ? { state } : undefined);
  };
  const goUseAutomation = async () => {
    const seg = segmentModal.created;
    if (!seg) return;
    let res;
    let json;
    try {
      res = await fetchWithAuthLogged("funnel", `${Utils.baseurl}automations/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: routeAppId, name: `${seg.name} automation`.slice(0, 150), include_segment_ids: [seg.id] }),
      });
      json = await res.json().catch(() => ({}));
    } catch {
      toast.error("Could not reach the server. Please try again.");
      return;
    }
    if (handleSessionExpired(res, json)) return;
    if (!json.status) {
      toast.error(json.message || "Could not create the automation");
      return;
    }
    goUseSegment(`automations/${json.data.id}`);
  };
  const changePage = (p) => {
    setUsersPage(p);
    loadUsers(usersModal.stepIndex, usersModal.kind, p, usersModal.group);
  };

  const exportUsers = async () => {
    if (!applied) return;
    setExporting(true);
    const { status, message } = await exportListCsv(
      "get/campaign/funnel/users",
      { ...applied.body, step_index: usersModal.stepIndex, kind: usersModal.kind, ...(usersModal.group ? { group: usersModal.group } : {}) },
      `funnel-${applied.body.steps[usersModal.stepIndex].replace(/[^a-z0-9_-]+/gi, "-")}-${usersModal.kind}.csv`
    );
    setExporting(false);
    if (message) toast[status ? "info" : "error"](message);
  };

  const exportSummary = () => {
    if (!result?.data?.length) return;
    const withAmount = result.data.some((d) => d.amount != null);
    const withEvents = result.data.some((d) => d.events != null);
    const head = ["Step", "Event", "Users", ...(withEvents ? ["Events"] : []), "% of step 1", "% from previous", "Dropped", "Avg time from previous", ...(withAmount ? ["Amount"] : [])];
    const lines = result.data.map((d, i) => [
      i + 1,
      labelizeKey(d.event),
      d.users,
      ...(withEvents ? [d.events ?? ""] : []),
      d.pct_of_first,
      i === 0 ? "" : d.pct_of_prev,
      i === 0 ? "" : d.dropped,
      i === 0 ? "" : formatDuration(d.avg_ms_from_prev),
      ...(withAmount ? [d.amount ?? ""] : []),
    ]);
    const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `funnel-${applied.body.steps.join("-").replace(/[^a-z0-9_-]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const moveStep = (i, d) =>
    setSteps((s) => {
      const j = i + d;
      if (j < 0 || j >= s.length) return s;
      const n = [...s];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });

  const onResetFilters = () => {
    setFilters((p) => ({ ...p, campaign_id: "", is_organic: "", country: "", platform: "", state: "", city: "", exclude_events: "" }));
  };

  const activeFilterCount = [filters.campaign_id, filters.is_organic, filters.country, filters.platform, filters.state, filters.city, filters.exclude_events].filter((v) => String(v || "") !== "").length;

  const data = useMemo(() => result?.data || [], [result]);
  const isStepTableAtEnd = useScrollEndDetection(stepTableRef, stepTableInnerRef, data.length);
  const isBreakdownAtEnd = useScrollEndDetection(breakdownTableRef, breakdownTableInnerRef, `${result?.breakdown?.by}:${result?.breakdown?.groups?.length}`);
  const summary = result?.summary;
  const appliedSteps = applied?.body?.steps || [];

  // Biggest leak between two consecutive steps — a marketer's first question.
  const biggestDrop = useMemo(() => {
    let best = null;
    data.forEach((d, i) => {
      if (i === 0 || !data[i - 1].users) return;
      const lost = 100 - (Number(d.pct_of_prev) || 0);
      if (!best || d.dropped > best.users || (d.dropped === best.users && lost > best.lost)) best = { i, lost, users: d.dropped };
    });
    return best;
  }, [data]);

  // Command bar chips: the filters set right now, each removable with ×.
  const filterChips = [
    campaignIds.length ? { key: "campaign_id", label: "Campaign", value: shortList(campaignIds.map((id) => campaignNames[id] || "…")) } : null,
    filters.is_organic !== "" ? { key: "is_organic", label: "Source", value: FUNNEL_SOURCE_OPTIONS.find((o) => o.value === String(filters.is_organic))?.label } : null,
    filters.platform ? { key: "platform", label: "Platform", value: filters.platform } : null,
    filters.country ? { key: "country", label: "Country", value: filters.country } : null,
    filters.state ? { key: "state", label: "State", value: filters.state } : null,
    filters.city ? { key: "city", label: "City", value: filters.city } : null,
    filters.exclude_events
      ? { key: "exclude_events", label: "Exclude", value: shortList(filters.exclude_events.split(",").filter(Boolean).map((e) => labelizeKey(e))) }
      : null,
  ].filter(Boolean);

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner funnel-page">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>
              {isNew ? "Create funnel" : name.trim() || "Funnel"}
              {!isNew && isDirty && <span className="badge rounded-pill bg-light-warning funnel-dirty-badge">Unsaved changes</span>}
            </h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {result && (
              <>
                <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh with latest data" onClick={onRefresh} disabled={loading}>
                  <FiRefreshCw />
                  <span>Refresh</span>
                </button>
                <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Download step summary as CSV" onClick={exportSummary}>
                  <MdFileDownload />
                  <span>Export</span>
                </button>
              </>
            )}
            {canManage && !funnelMissing && (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                title={isNew ? "Save this funnel with its filters" : "Save changes to steps and filters"}
                onClick={() => saveFunnel()}
                disabled={saving || funnelLoading || !appUuid || (!isNew && !isDirty)}
              >
                <MdSave />
                <span>{saving ? "Saving…" : isNew ? "Save funnel" : "Save changes"}</span>
              </button>
            )}
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={goBack}>
              <MdArrowBack />
              <span>Back</span>
            </button>
          </div>
        </div>

        {funnelMissing ? (
          <div className="funnel-card funnel-empty">
            <MdPeopleOutline size={38} />
            <h3>Funnel not found</h3>
            <p>It may have been deleted. Go back to the list to open another funnel.</p>
          </div>
        ) : funnelLoading ? (
          <div className="funnel-card">
            <SkeletonLoader columns={[1, 2, 3, 4]} rows={4} />
          </div>
        ) : (
        <>

        {/* ---------- command bar: steps, settings, filters (popover) and the filters in use ---------- */}
        <div className="funnel-card funnel-bar">
          <div className="funnel-bar-row">
            <span className="funnel-bar-label">
              Steps <HelpTooltip size={14} text="Choose the events in the order users should do them (for example: Install → Signup → Booking). A step can have more than one event." />
            </span>
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <MdArrowForward className="funnel-bar-arrow" aria-hidden="true" />}
                <div className="funnel-pop-anchor">
                  <button
                    type="button"
                    className={`funnel-step-pill ${!s ? "is-empty" : ""} ${!s && missingStep === i ? "is-missing" : ""} ${s && isDupStep(s, i) ? "is-dup" : ""} ${pop === `step-${i}` ? "is-open" : ""}`}
                    aria-expanded={pop === `step-${i}`}
                    onClick={() => togglePop(`step-${i}`)}
                  >
                    <span className="funnel-step-num">{i + 1}</span>
                    <span className="funnel-step-pill-text">{s ? splitStep(s).map((p) => labelizeKey(p)).join(" / ") : "Select event"}</span>
                    <MdKeyboardArrowDown />
                  </button>
                  {pop === `step-${i}` && (
                    <FunnelPopover className="funnel-pop--step" role="dialog" aria-label={`Step ${i + 1}`}>
                      <div className="funnel-pop-head">
                        <strong>Step {i + 1}</strong>
                        <div className="funnel-step-actions">
                          <button type="button" title="Move left" disabled={i === 0} onClick={() => { moveStep(i, -1); setPop(`step-${i - 1}`); }}><MdArrowBack /></button>
                          <button type="button" title="Move right" disabled={i === steps.length - 1} onClick={() => { moveStep(i, 1); setPop(`step-${i + 1}`); }}><MdArrowForward /></button>
                          <button type="button" title="Remove step" disabled={steps.length <= FUNNEL_MIN_STEPS} onClick={() => { setSteps((arr) => arr.filter((_, k) => k !== i)); setPop(null); }}><MdClose /></button>
                        </div>
                      </div>
                      <AsyncSearchSelect
                        inputId={`funnel-step-${i}`}
                        instanceId={`funnel-step-${i}`}
                        menuPlacement="auto"
                        classNamePrefix="ns-funnel-step"
                        placeholder="Select event(s)…"
                        enabled={Boolean(appUuid)}
                        fetchOptions={stepOptsFor(i)}
                        isMulti
                        isClearable={false}
                        value={splitStep(s)}
                        onChange={(opts) => setSteps((arr) => arr.map((x, k) => (k === i ? (opts || []).map((o) => o.value).join(FUNNEL_STEP_SEPARATOR) : x)))}
                        styles={selectStyles}
                      />
                      <small className="funnel-pop-hint">Pick more than one event if any of them should count for this step.</small>
                    </FunnelPopover>
                  )}
                </div>
              </React.Fragment>
            ))}
            {steps.length < FUNNEL_MAX_STEPS && (
              <button type="button" className="funnel-step-pill funnel-step-pill--add" onClick={() => { setSteps((arr) => [...arr, ""]); setPop(`step-${steps.length}`); }}>
                <MdAdd size={16} />
                <span>Add step</span>
              </button>
            )}
          </div>
          {duplicate && <div className="funnel-error funnel-bar-error">An event can be used in only one step.</div>}

          <div className="funnel-bar-row funnel-bar-row--settings">
            <div className="funnel-bar-group">
              <div className="funnel-bar-date">
                <DateRangePicker
                  label=""
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  preset={filters.datePreset}
                  presets={DATE_RANGE_PRESETS}
                  allowClear={false}
                  onChange={({ dateFrom, dateTo, preset }) =>
                    setFilters((p) => ({ ...p, dateFrom: dateFrom || "", dateTo: dateTo || "", datePreset: preset || "" }))
                  }
                />
              </div>
              <span className="funnel-bar-divider" aria-hidden="true" />
              <div className="funnel-seg funnel-seg--inline funnel-bar-seg" role="group" aria-label="Step order">
                {FUNNEL_MODE_CODES.map((m) => (
                  <button key={m.key} type="button" className={mode === m.key ? "active" : ""} onClick={() => setMode(m.key)}>{m.label}</button>
                ))}
                <HelpTooltip size={14} text="In order: a user counts for a step only if they did the earlier steps first. Strict: like In order, but no other funnel step may happen in between (other app events are fine). Any order: a user counts once they have done all the steps up to that one, in any order." />
              </div>
              <div className="funnel-bar-select" title="How long a user has, from their first step, to finish the rest">
                <Select
                  inputId="funnel-window"
                  instanceId="funnel-window"
                  classNamePrefix="ns-funnel-window"
                  options={FUNNEL_WINDOW_OPTIONS}
                  value={FUNNEL_WINDOW_OPTIONS.find((o) => o.value === windowDays) || FUNNEL_WINDOW_OPTIONS[0]}
                  onChange={(o) => setWindowDays(o?.value || "")}
                  styles={barSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
              <div className="funnel-bar-select funnel-bar-select--wide" title="Show the funnel side by side for each group">
                <Select
                  inputId="funnel-breakdown"
                  instanceId="funnel-breakdown"
                  classNamePrefix="ns-funnel-breakdown"
                  options={FUNNEL_BREAKDOWN_OPTIONS}
                  value={FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === (filters.breakdown || "")) || FUNNEL_BREAKDOWN_OPTIONS[0]}
                  onChange={(o) => setFilters((p) => ({ ...p, breakdown: o?.value || "" }))}
                  formatOptionLabel={(o, { context }) =>
                    context === "value" && o.value ? (
                      <>
                        <span className="funnel-bar-prefix">Breakdown by</span> {o.label}
                      </>
                    ) : (
                      o.label
                    )
                  }
                  styles={barSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
              <span className="funnel-bar-divider" aria-hidden="true" />
              <div className="funnel-pop-anchor">
                <button
                  type="button"
                  className={`add_app_btn_secondary btn_theme_secondary funnel-filter-btn ${pop === "filters" || activeFilterCount ? "is-active" : ""}`}
                  aria-expanded={pop === "filters"}
                  onClick={() => togglePop("filters")}
                >
                  <MdFilterAlt />
                  <span>Filters</span>
                  {activeFilterCount > 0 && <span className="funnel-filter-count">{activeFilterCount}</span>}
                </button>
                {pop === "filters" && (
                  <FunnelPopover className="funnel-pop--filters" prefer="right" role="dialog" aria-label="Filters">
                    <div className="funnel-pop-nav">
                      {FILTER_GROUPS.map((g) => {
                        const n = g.keys.filter((k) => String(filters[k] || "") !== "").length;
                        return (
                          <button key={g.key} type="button" className={popTab === g.key ? "active" : ""} onClick={() => setPopTab(g.key)}>
                            {g.label}
                            {n > 0 && <span className="funnel-filter-count">{n}</span>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="funnel-pop-body">
                      {popTab === "audience" && (
                        <>
                          <label className="funnel-pop-field">
                            <span>Campaign</span>
                            <AsyncSearchSelect
                              inputId="funnel-campaign"
                              instanceId="funnel-campaign"
                        menuPlacement="auto"
                              classNamePrefix="ns-funnel-campaign"
                              isMulti
                              isClearable
                              placeholder="All campaigns"
                              enabled={Boolean(appUuid)}
                              fetchOptions={fetchCampaignOpts}
                              value={campaignIds}
                              onChange={(opts) => {
                                const list = opts || [];
                                setFilters((p) => ({ ...p, campaign_id: list.map((o) => o.value).join(",") }));
                                setCampaignNames((m) => ({ ...m, ...Object.fromEntries(list.map((o) => [String(o.value), o.label])) }));
                              }}
                              styles={selectStyles}
                            />
                          </label>
                          <div className="funnel-pop-grid">
                            <label className="funnel-pop-field">
                              <span>Source</span>
                              <Select
                                inputId="funnel-source"
                                instanceId="funnel-source"
                                classNamePrefix="ns-funnel-source"
                                options={FUNNEL_SOURCE_OPTIONS.filter((o) => o.value !== "")}
                                isSearchable={false}
                                isClearable
                                placeholder="All sources"
                                value={FUNNEL_SOURCE_OPTIONS.find((o) => o.value !== "" && o.value === String(filters.is_organic)) || null}
                                onChange={(o) => setFilters((p) => ({ ...p, is_organic: o?.value ?? "" }))}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                styles={selectStyles}
                              />
                            </label>
                            <label className="funnel-pop-field">
                              <span>Platform</span>
                              <AsyncSearchSelect
                                inputId="funnel-platform"
                                instanceId="funnel-platform"
                        menuPlacement="auto"
                                classNamePrefix="ns-funnel-platform"
                                isClearable
                                placeholder="All platforms"
                                enabled={Boolean(appUuid)}
                                fetchOptions={fetchPlatformOpts}
                                value={filters.platform || null}
                                onChange={(opt) => setFilters((p) => ({ ...p, platform: opt?.value ?? "" }))}
                                styles={selectStyles}
                              />
                            </label>
                          </div>
                        </>
                      )}
                      {popTab === "location" && (
                        <>
                          <label className="funnel-pop-field">
                            <span>Country</span>
                            <AsyncSearchSelect
                              inputId="funnel-country"
                              instanceId="funnel-country"
                        menuPlacement="auto"
                              classNamePrefix="ns-funnel-country"
                              isClearable
                              placeholder="All countries"
                              enabled={Boolean(appUuid)}
                              fetchOptions={fetchCountryOpts}
                              value={filters.country || null}
                              onChange={(opt) => setFilters((p) => ({ ...p, country: opt?.value ?? "" }))}
                              styles={selectStyles}
                            />
                          </label>
                          <div className="funnel-pop-grid">
                            <label className="funnel-pop-field">
                              <span>State</span>
                              <AsyncSearchSelect
                                inputId="funnel-state"
                                instanceId="funnel-state"
                        menuPlacement="auto"
                                classNamePrefix="ns-funnel-state"
                                isClearable
                                placeholder="All states"
                                enabled={Boolean(appUuid)}
                                fetchOptions={fetchStateOpts}
                                value={filters.state || null}
                                onChange={(opt) => setFilters((p) => ({ ...p, state: opt?.value ?? "" }))}
                                styles={selectStyles}
                              />
                            </label>
                            <label className="funnel-pop-field">
                              <span>City</span>
                              <AsyncSearchSelect
                                inputId="funnel-city"
                                instanceId="funnel-city"
                        menuPlacement="auto"
                                classNamePrefix="ns-funnel-city"
                                isClearable
                                placeholder="All cities"
                                enabled={Boolean(appUuid)}
                                fetchOptions={fetchCityOpts}
                                value={filters.city || null}
                                onChange={(opt) => setFilters((p) => ({ ...p, city: opt?.value ?? "" }))}
                                styles={selectStyles}
                              />
                            </label>
                          </div>
                          <small className="funnel-pop-hint">State and City use the user's profile, so anonymous (not logged in) users are left out.</small>
                        </>
                      )}
                      {popTab === "exclude" && (
                        <>
                          <label className="funnel-pop-field">
                            <span>Exclude users who did</span>
                            <AsyncSearchSelect
                              inputId="funnel-exclude"
                              instanceId="funnel-exclude"
                        menuPlacement="auto"
                              classNamePrefix="ns-funnel-exclude"
                              isMulti
                              isClearable
                              placeholder="No exclusions"
                              enabled={Boolean(appUuid)}
                              fetchOptions={fetchExcludeOpts}
                              value={splitStep(String(filters.exclude_events || "").split(",").join(FUNNEL_STEP_SEPARATOR))}
                              onChange={(opts) => setFilters((p) => ({ ...p, exclude_events: (opts || []).map((o) => o.value).join(",") }))}
                              styles={selectStyles}
                            />
                          </label>
                          <small className="funnel-pop-hint">Users who did any of these after their first step are left out completely (for example Uninstall).</small>
                        </>
                      )}
                      <div className="funnel-pop-foot">
                        <button type="button" className="btn_theme_secondary" onClick={onResetFilters}>Clear</button>
                        <button type="button" className="add_app_btn btn_theme_primary" onClick={() => { setPop(null); runFunnel(); }} disabled={loading || !appUuid}>
                          <span>Show funnel</span>
                        </button>
                      </div>
                    </div>
                  </FunnelPopover>
                )}
              </div>
            </div>
            <div className="funnel-bar-group">
              <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters} disabled={loading}>
                <MdRefresh />
                <span>Reset</span>
              </button>
              <button type="button" className="add_app_btn btn_theme_primary" onClick={runFunnel} disabled={loading || !appUuid}>
                <MdSearch />
                <span>{loading ? "Building…" : "Show funnel"}</span>
              </button>
            </div>
          </div>

          {filterChips.length > 0 && (
            <div className="funnel-bar-chips">
              {filterChips.map((c) => (
                <span key={c.key} className="funnel-chip funnel-chip--remove">
                  <span className="funnel-chip-key">{c.label}:</span> {c.value}
                  <button type="button" aria-label={`Remove ${c.label} filter`} onClick={() => setFilters((p) => ({ ...p, [c.key]: "" }))}>
                    <MdClose size={13} />
                  </button>
                </span>
              ))}
              <button type="button" className="funnel-bar-clear" onClick={onResetFilters}>Clear all</button>
            </div>
          )}
        </div>

        {/* ---------- results ---------- */}
        {loading && !result && (
          <div className="funnel-card">
            <div className="app-dashboard__chart-skeleton skeleton-cell" aria-hidden="true" />
          </div>
        )}

        {!result && !loading && (
          <div className="funnel-card funnel-empty">
            <div className="funnel-empty-art" aria-hidden="true">
              <span style={{ width: "100%" }} />
              <span style={{ width: "70%" }} />
              <span style={{ width: "42%" }} />
            </div>
            <h3>Build your first funnel</h3>
            <p>Pick at least two events above and click <strong>Show funnel</strong>. Then <strong>Save funnel</strong> to keep it with its filters in the funnel list.</p>
          </div>
        )}

        {result && (
          <div className={`funnel-results ${loading ? "is-loading" : ""}`}>
            {summary.truncated && (
              <div className="alert alert-warning py-2">
                Very large date range — figures use the first 500,000 events. Pick a shorter range for exact numbers.
              </div>
            )}

            <div className="campaign-summary-row">
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Started ({labelizeKey(appliedSteps[0])}) <HelpTooltip size={15} text={KPI_HELP.started} />
                </span>
                <strong className="campaign-summary-value" title={countFormat(summary.entered)}>{bigAmountFormat(summary.entered)}</strong>
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Completed ({labelizeKey(appliedSteps[appliedSteps.length - 1])}) <HelpTooltip size={15} text={KPI_HELP.completed} />
                </span>
                <strong className="campaign-summary-value" title={countFormat(summary.completed)}>{bigAmountFormat(summary.completed)}</strong>
                {data[data.length - 1]?.amount != null && (
                  <div className="funnel-kpi-sub">{money(data[data.length - 1].amount)}{data[data.length - 1].events != null ? ` (${countFormat(data[data.length - 1].events)})` : ""} total in range</div>
                )}
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Overall conversion <HelpTooltip size={15} text={KPI_HELP.conversion} />
                </span>
                <strong className="campaign-summary-value">{pct(summary.overall_conversion)}</strong>
                <div className="funnel-meter-bar"><i style={{ width: `${Math.min(100, summary.overall_conversion)}%` }} /></div>
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  Biggest drop-off <HelpTooltip size={15} text={KPI_HELP.drop} />
                </span>
                {biggestDrop && biggestDrop.users > 0 ? (
                  <>
                    <strong className="campaign-summary-value" title={plural(biggestDrop.users, "user")}>{plural(biggestDrop.users, "user", bigAmountFormat)}</strong>
                    <small className="text-muted">
                      {labelizeKey(data[biggestDrop.i - 1].event)} → {labelizeKey(data[biggestDrop.i].event)} · {pct(biggestDrop.lost)} lost
                    </small>
                  </>
                ) : (
                  <strong className="campaign-summary-value">—</strong>
                )}
              </div>
              <div className="campaign-summary-card">
                <span className="campaign-summary-label">
                  {applied?.mode === "any" ? "Avg span to complete" : "Avg time to complete"} <HelpTooltip size={15} text={KPI_HELP.time} />
                </span>
                <strong className="campaign-summary-value">{formatDuration(summary.avg_ms_total)}</strong>
                {summary.median_ms_total != null && <div className="funnel-kpi-sub">Median {formatDuration(summary.median_ms_total)}</div>}
              </div>
            </div>

            {summary.entered === 0 ? (
              <div className="funnel-card funnel-empty">
                <MdPeopleOutline size={38} />
                <h3>No users did {labelizeKey(appliedSteps[0])} in this period</h3>
                <p>Try a longer date range or remove some filters.</p>
              </div>
            ) : (
              <>
                <div className="funnel-insight">
                  <strong>{plural(summary.entered, "user")}</strong> did {labelizeKey(appliedSteps[0])}.{" "}
                  <strong>{countFormat(summary.completed)}</strong> ({pct(summary.overall_conversion)}) went all the way to{" "}
                  {labelizeKey(appliedSteps[appliedSteps.length - 1])}.
                  {applied?.filters?.datePreset && applied.filters.datePreset !== "all_time" && summary.entered <= 1 && (
                    <>
                      {" "}Only a few users in this period. Try a longer date range to see more.
                    </>
                  )}
                  {summary.completed > 0 && summary.completed < 5 && (
                    <>
                      {" "}Only {plural(summary.completed, "user")} finished, so the rates and times below can change a lot as more data comes in.
                    </>
                  )}
                  {biggestDrop && biggestDrop.users > 0 && (
                    <>
                      {" "}The most users are lost between <strong>{labelizeKey(data[biggestDrop.i - 1].event)}</strong> and{" "}
                      <strong>{labelizeKey(data[biggestDrop.i].event)}</strong> ({plural(biggestDrop.users, "user")}, {pct(biggestDrop.lost)}).
                    </>
                  )}
                </div>

                <FunnelChartTabs
                  result={result}
                  steps={appliedSteps}
                  leaks={leaks.key === leaksKey ? leaks.data : null}
                  onNeedLeaks={loadLeaks}
                  onOpen={openUsers}
                />

                <div className="funnel-card">
                  <div className="funnel-card-head">
                    <div>
                      <h2>Step breakdown</h2>
                      <p>Open any step to see exactly which users reached it or dropped off.</p>
                    </div>
                  </div>
                  <div className="dashboard_app_table dashboard_app_table--list-records">
                   <div className="dashboard_table">
                    <div ref={stepTableRef} className="table_scroll_wrapper">
                     <div ref={stepTableInnerRef} className={`table_inner ${isStepTableAtEnd ? "at-scroll-end" : ""}`} style={{ overflowX: "auto" }}>
                    <table className="table_outer table-sm">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th className="text-end">Users</th>
                          <th className="text-end">All events <HelpTooltip size={13} text={"Every event of this type in the date range, from all users (not only the users in this funnel) — the same number the Campaign pages show. Not available with a Platform, State, City or Exclude filter."} /></th>
                          <th style={{ minWidth: 180 }}>Of step 1</th>
                          <th className="text-end">From previous</th>
                          <th className="text-end">Lost from previous</th>
                          <th className="text-end">Avg time from previous</th>
                          <th className="text-end">Median time</th>
                          {data.some((x) => x.amount != null) && <th className="text-end">Amount <HelpTooltip size={13} text="Total amount of all revenue events of this step in the date range (all users), the same as the Campaign pages." /></th>}
                          <th className="col-action" style={{ textAlign: "center" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((d, i) => {
                          return (
                            <tr key={d.event}>
                              <td>
                                <div className="funnel-table-step">
                                  <span className="funnel-step-num">{i + 1}</span>
                                  <strong>{labelizeKey(d.event)}</strong>
                                </div>
                                {stepEvents(d) && <StepEventList events={stepEvents(d)} />}
                              </td>
                              <td className="text-end"><strong>{countFormat(d.users)}</strong></td>
                              <td className="text-end" title={d.events == null ? "Not available with a Platform, State, City or Exclude filter" : undefined}>{d.events != null ? countFormat(d.events) : "—"}</td>
                              <td>
                                <div className="funnel-meter">
                                  <div className="funnel-meter-bar"><i style={{ width: `${d.pct_of_first}%` }} /></div>
                                  <span>{pct(d.pct_of_first)}</span>
                                </div>
                              </td>
                              <td className="text-end">{i === 0 ? "—" : pct(d.pct_of_prev)}</td>
                              <td className="text-end">{i === 0 ? "—" : d.dropped ? `−${countFormat(d.dropped)}` : "0"}</td>
                              <td className="text-end">{i === 0 || d.avg_ms_from_prev == null ? "—" : formatDuration(d.avg_ms_from_prev)}</td>
                              <td className="text-end">{i === 0 || d.median_ms_from_prev == null ? "—" : formatDuration(d.median_ms_from_prev)}</td>
                              {data.some((x) => x.amount != null) && (
                                <td className="text-end">{d.amount != null ? <strong>{money(d.amount)}{d.events != null ? ` (${countFormat(d.events)})` : ""}</strong> : "—"}</td>
                              )}
                              <td className="col-action" style={{ textAlign: "center" }}>
                                <button
                                  type="button"
                                  className="dropdown-trigger-btn btn btn-link p-0"
                                  aria-label="Actions"
                                  aria-expanded={openActionId === d.event}
                                  onClick={(e) => handleOpenDropdown(e, d.event, { i, users: d.users, kind: "reached", dropped: data[i + 1]?.dropped || 0, next: data[i + 1]?.event })}
                                >
                                  <BsThreeDotsVertical />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                     </div>
                    </div>
                   </div>
                  </div>
                </div>
                {result.breakdown?.groups?.length > 0 && (
                  <div className="funnel-card">
                    <div className="funnel-card-head">
                      <div>
                        <h2>Breakdown by {FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === result.breakdown.by)?.label || result.breakdown.by}</h2>
                        <p>The same funnel, split by group. Each cell is the users who reached that step and their share of the group's first step.</p>
                      </div>
                    </div>
                    <div className="dashboard_app_table dashboard_app_table--list-records">
                      <div className="dashboard_table">
                        <div ref={breakdownTableRef} className="table_scroll_wrapper">
                          <div ref={breakdownTableInnerRef} className={`table_inner ${isBreakdownAtEnd ? "at-scroll-end" : ""}`} style={{ overflowX: "auto" }}>
                            <table className="table_outer table-sm">
                              <thead>
                                <tr>
                                  <th>{FUNNEL_BREAKDOWN_OPTIONS.find((o) => o.value === result.breakdown.by)?.label || "Group"}</th>
                                  {appliedSteps.map((s, i) => (
                                    <th key={s} className="text-end">{i + 1}. {labelizeKey(s)}</th>
                                  ))}
                                  <th className="text-end">Overall conversion</th>
                                  <th className="col-action" style={{ textAlign: "center" }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.breakdown.groups.map((g) => (
                                  <tr key={g.label}>
                                    <td><strong>{g.label}</strong></td>
                                    {g.users.map((n, i) => (
                                      <td key={i} className="text-end">
                                        <strong>{countFormat(n)}</strong>
                                        {i > 0 && <small className="text-muted"> · {pct(g.pct_of_first[i])}</small>}
                                      </td>
                                    ))}
                                    <td className="text-end"><strong>{pct(g.pct_of_first[g.pct_of_first.length - 1])}</strong></td>
                                    <td className="col-action" style={{ textAlign: "center" }}>
                                      <button
                                        type="button"
                                        className="dropdown-trigger-btn btn btn-link p-0"
                                        aria-label="Actions"
                                        aria-expanded={openActionId === `bd:${g.label}`}
                                        onClick={(e) => handleOpenDropdown(e, `bd:${g.label}`, { i: 0, users: g.users[0], group: g.label, kind: "all" })}
                                      >
                                        <BsThreeDotsVertical />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </>
        )}

        {openActionItem && dropdownPosition
          ? createPortal(
              <div ref={dropdownRef} style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: "200px" }}>
                <div className="dropdown-menu-subscription">
                  <button
                    type="button"
                    disabled={!openActionItem.users}
                    onClick={() => handleDropdownAction(() => openUsers(openActionItem.i, openActionItem.kind, openActionItem.group || ""))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <FiExternalLink size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>{openActionItem.kind === "all" ? "View users" : "View users who reached"}</span>
                  </button>
                  {openActionItem.kind === "reached" && openActionItem.next && (
                    <button
                      type="button"
                      disabled={!openActionItem.dropped}
                      onClick={() => handleDropdownAction(() => openUsers(openActionItem.i, "dropped"))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                    >
                      <FiExternalLink size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.875rem" }}>View dropped before {labelizeKey(openActionItem.next)}</span>
                    </button>
                  )}
                </div>
              </div>,
              document.body
            )
          : null}
        <FunnelUsersModal
          mode={applied?.mode}
          show={usersModal.show}
          onHide={() => setUsersModal((m) => ({ ...m, show: false }))}
          stepIndex={usersModal.stepIndex}
          kind={usersModal.kind}
          group={usersModal.group}
          steps={appliedSteps}
          data={data}
          rows={usersData.rows}
          meta={usersData.meta}
          loading={usersLoading}
          error={usersError}
          onRetry={() => loadUsers(usersModal.stepIndex, usersModal.kind, usersPage, usersModal.group)}
          page={usersPage}
          onPage={changePage}
          onExport={exportUsers}
          exporting={exporting}
          onCreateSegment={can("segments.add") ? openSegmentModal : null}
        />

        <FunnelDetailsModal
          show={detailsOpen}
          title="Save funnel"
          initialName={name}
          initialDescription={description}
          saving={saving}
          saveLabel="Save funnel"
          onCancel={() => setDetailsOpen(false)}
          onSave={(n, d) => saveFunnel(n, d)}
        />

        <Modal show={segmentModal.show} onHide={() => setSegmentModal((m) => ({ ...m, show: false }))} className="ns-modal" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {segmentModal.created ? "Segment ready" : "Message these users"}
              <small className="funnel-users-sub">
                {segmentModal.created
                  ? "Pick where to use it"
                  : "Saves these users as a segment, which Push, WhatsApp and Automations can use as their audience."}
              </small>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {segmentModal.created ? (
              <>
                <p className="mb-2">
                  <strong>{segmentModal.created.name}</strong> has {plural(segmentModal.created.users, "user")} ({plural(segmentModal.created.subscriptions, "reachable device")}).
                </p>
                {segmentModal.created.reused && (
                  <div className="alert alert-info py-2">These exact users were already saved, so the existing segment is used (no duplicate created).</div>
                )}
                {segmentModal.created.capped && (
                  <div className="alert alert-warning py-2">Only the {countFormat(segmentModal.created.users)} newest of {countFormat(segmentModal.created.users_total)} users were added (segment limit).</div>
                )}
                {segmentModal.created.anonymous_skipped > 0 && (
                  <div className="alert alert-light border py-2">{plural(segmentModal.created.anonymous_skipped, "anonymous user")} left out — they are not logged in, so a segment cannot address them.</div>
                )}
                <div className="funnel-segment-actions">
                  {can("push.view") && (
                    <button type="button" className="btn_theme_primary" onClick={() => goUseSegment("push-notification", { presetSegmentIds: [segmentModal.created.id] })}>Send push</button>
                  )}
                  {can("whatsapp.send.view") && (
                    <button type="button" className="btn_theme_primary" onClick={() => goUseSegment("whatsapp/bulk", { presetSegmentIds: [segmentModal.created.id] })}>Send WhatsApp</button>
                  )}
                  {can("automation.view") && (
                    <button type="button" className="btn_theme_secondary" onClick={goUseAutomation}>Use in an Automation</button>
                  )}
                  <button type="button" className="btn_theme_secondary" onClick={() => goUseSegment("segments")}>View segments</button>
                </div>
              </>
            ) : (
              <>
                <label className="form-label">Segment name</label>
                <input
                  type="text"
                  className="form-control"
                  value={segmentModal.name}
                  maxLength={150}
                  onChange={(e) => setSegmentModal((m) => ({ ...m, name: e.target.value }))}
                  autoFocus
                />
                <small className="text-muted d-block mt-2">
                  A fixed list of the {usersModal.kind === "dropped" ? "dropped" : usersModal.kind === "all" ? "starting" : "reached"} users right now (logged-in users only, up to {FUNNEL_SEGMENT_USER_CAP.toLocaleString("en-IN")}).
                </small>
              </>
            )}
          </Modal.Body>
          {!segmentModal.created && (
            <Modal.Footer>
              <button type="button" className="btn_theme_secondary" onClick={() => setSegmentModal((m) => ({ ...m, show: false }))}>Cancel</button>
              <button type="button" className="btn_theme_primary" onClick={createSegment} disabled={segmentModal.saving}>
                {segmentModal.saving ? "Creating…" : "Create segment"}
              </button>
            </Modal.Footer>
          )}
        </Modal>
      </div>
    </div>
  );
}
